import * as maptilersdk from '@maptiler/sdk';
import Spiderfy from '@nazka/map-gl-js-spiderfy';
import { useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { addMarkerImagesToMap } from './map.utils';

import sources from './style/style.sources';
import layers from './style/style.layers';

import '@maptiler/sdk/dist/maptiler-sdk.css';
import './map.styl';

maptilersdk.config.apiKey = import.meta.env.VITE_MAPTILER_API_KEY;

function Map({
  map, setMap, setClickedFeature, setHoveredFeature,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mapContainer = useRef(null);
  const spiderfy = useRef(null);
  const lastHoveredFeature = useRef();
  const lastHoveredSpiderFeatureSource = useRef();
  const homeAddress = useSelector(state => state.map.homeAddress);
  const { OIDN } = useSelector(state => state.vacancy);

  useEffect(() => {
    if (map) return;
    
    const initMap = new maptilersdk.Map({
      container: mapContainer.current,
      style: '7c7920df-8e09-46ea-a0e8-8a39767cd575',
      center: [4.5, 50.5],
      zoom: 7,
      maxZoom: 18,
      fadeDuration: 0,
      navigationControl: false,
      geolocateControl: false,
      attributionControlOptions: false,
      forceNoAttributionControl: true,
    });

    initMap.addControl(new maptilersdk.MaptilerNavigationControl({
      showCompass: false,
    }), 'bottom-right');

    initMap.addControl(new maptilersdk.AttributionControl({
      customAttribution: '<a href="https://nazka.be" rel="noopener" target="_blank">Nazka Mapps</a>',
      compact: false,
    }), 'bottom-right');

    initMap.on('load', async () => {
      Object.keys(sources).forEach((sourceId) => {
        initMap.addSource(sourceId, sources[sourceId]);
      });
      await addMarkerImagesToMap(initMap);
      layers.forEach(layer => initMap.addLayer(layer));

      initMap.on('mousemove', (e) => {
        if (initMap.getCanvas().hasAttribute('data-spider-leaf-hover')) return;

        const features = initMap.queryRenderedFeatures(e.point);
      
        if (features.length > 0) {
          const vacancyFeature = features.find((f) => {
            return ['vacancies-clusters', 'vacancies'].includes(f.layer.id);
          });

          const transportFeature = features.find((f) => {
            return ['nearby-stops', 'hovered-nearby-stop', 'fietssnelwegen-hover'].includes(f.layer.id);
          });

          if (lastHoveredFeature.current) {
            initMap.setFeatureState(lastHoveredFeature.current, { highlight: false });
          }

          if (vacancyFeature) {
            setHoveredFeature(vacancyFeature);
            initMap.getCanvas().style.cursor = 'pointer';
            initMap.setFeatureState({ source: 'vacancies', id: vacancyFeature.id }, { highlight: true });
            lastHoveredFeature.current = { source: 'vacancies', id: vacancyFeature.id };
          } else if (transportFeature) {
            initMap.getCanvas().style.cursor = 'pointer';
          } else {
            initMap.getCanvas().style.cursor = '';
            setHoveredFeature(null);
          }
        } else {
          initMap.getCanvas().style.cursor = '';
          setHoveredFeature(null);
        }
      });

      initMap.on('click', async (e) => {
        const features = initMap.queryRenderedFeatures(e.point);

        if (features.length > 0) {
          const clusterLeaves = Array(200).fill(0).map((_, i) => `vacancies-clusters-spiderfy-leaf${i}`);

          if ([...clusterLeaves, 'vacancies'].includes(features?.[0]?.layer?.id)) {
            if (features.length) setClickedFeature(features[0]);
            document.querySelector('.shared-base__container__left')?.scrollIntoView({ behavior: 'smooth' });
          }
      
          if (features?.[0]?.layer?.id === 'vacancies-clusters') {
            if (initMap.getZoom() >= 19) return; 

            const clusterId = features[0].properties.cluster_id;
            const clusterCoords = features[0].geometry.coordinates;
            const expansionZoom = await initMap.getSource('vacancies').getClusterExpansionZoom(clusterId);

            initMap.flyTo({
              center: clusterCoords,
              zoom: expansionZoom,
              speed: 1,
            });
          }
        }
      });

      initMap.on('zoomend', async () => {
        setHoveredFeature(null);
      });

      spiderfy.current = new Spiderfy(initMap, {
        minZoomLevel: 17.9,
        zoomIncrement: 0,
        closeOnLeafClick: false,
        spiderLeavesLayout: {
          ...layers.find(l => l.id === 'vacancies').layout,
          'icon-image': [
            'case',
            ['all', ['==', ['get', 'isJobAggregator'], true], ['==', ['get', 'highlight'], true]],
            'intrim-with-counter-highlighted',
            ['==', ['get', 'isJobAggregator'], true],
            'intrim-with-counter',
            ['all', ['==', ['get', 'hasHouseNb'], false], ['==', ['get', 'highlight'], true]],
            'work-no-address-with-counter-highlighted',
            ['==', ['get', 'hasHouseNb'], false],
            'work-no-address-with-counter',
            ['==', ['get', 'highlight'], true],
            'work-with-counter-highlighted',
            'work-with-counter',
          ],
        },
        spiderLeavesPaint: {
          ...layers.find(l => l.id === 'vacancies').paint,
        },
        renderMethod: '3D',
        spiralOptions: { legLengthStart: 28, leavesSeparation: 38, legLengthFactor: 3.5 },
        onLeafHover: (feature, event) => {
          const canvas = event.target.getCanvas();
          if (lastHoveredFeature.current) {
            initMap.setFeatureState(lastHoveredFeature.current, { highlight: false });
            lastHoveredFeature.current = null;
          }

          if (lastHoveredSpiderFeatureSource.current) {
            const source = initMap.getSource(lastHoveredSpiderFeatureSource.current);
            source.setData({
              ...source._data,
              features: source._data.features.map(f => ({
                ...f,
                properties: { ...f.properties, highlight: false },
              })),
            });
            lastHoveredSpiderFeatureSource.current = null;
          }
          
          if (feature) {
            canvas.setAttribute('data-spider-leaf-hover', '');
            canvas.style.cursor = 'pointer';
            const features = initMap.queryRenderedFeatures(event.point);

            setHoveredFeature({ ...feature, id: feature?.properties?.OIDN });

            const source = initMap.getSource(features?.[0]?.source);
            lastHoveredSpiderFeatureSource.current = features?.[0]?.source;

            if (source) {
              source.setData({
                ...source._data,
                features: source._data.features.map(f => ({
                  ...f,
                  properties: { ...f.properties, highlight: true },
                })),
              });
            }
          } else {
            canvas.removeAttribute('data-spider-leaf-hover');
            canvas.style.cursor = '';
          }
        },
        onLeafClick: (feature) => {
          setClickedFeature({ ...feature, id: feature?.properties?.OIDN });
        },
      });
      spiderfy.current.applyTo('vacancies-clusters');

      setMap(initMap);
    });
  }, [map, setMap, homeAddress, dispatch, navigate, setHoveredFeature, setClickedFeature]);

  useEffect(() => {
    if (!map || !map.getSource('home')) return;
    map.getSource('home').setData({
      type: 'FeatureCollection',
      features: [homeAddress],
    });
  }, [map, homeAddress]);

  useEffect(() => {
    if (!spiderfy.current) return;

    if (OIDN) {
      spiderfy.current.unspiderfyAll();
    } else {
      spiderfy.current.applyTo('vacancies-clusters');
    }
  }, [OIDN]);

  return (
    <div
      className="map"
      ref={mapContainer} 
      onMouseLeave={() => {
        if (map && lastHoveredFeature.current) {
          map.setFeatureState(lastHoveredFeature.current, { highlight: false });
          setClickedFeature([]);
        }
      }}
    />
  )
}

export default Map;
